import { useState, useEffect } from 'react';
import { Check, X, Star, Trash2, MoreVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { adminApi } from '../../lib/api';
import { formatDate, truncateText } from '../../lib/utils';
import { toast } from 'sonner';

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const response = await adminApi.getTestimonials();
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error loading testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await adminApi.approveTestimonial(id);
      toast.success('Testimonial approved');
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to approve testimonial');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.deleteTestimonial(id);
      toast.success('Testimonial deleted');
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Testimonials</h1>
          <p className="text-[#4A4A4A]">Manage customer testimonials</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Package</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Review</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
              </TableRow>
            ) : testimonials.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">No testimonials yet</TableCell>
              </TableRow>
            ) : (
              testimonials.map((testimonial) => (
                <TableRow key={testimonial.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {testimonial.photo ? (
                        <img src={testimonial.photo} alt={testimonial.customer_name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#004E64] flex items-center justify-center text-white font-semibold">
                          {testimonial.customer_name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{testimonial.customer_name}</p>
                        <p className="text-sm text-[#4A4A4A]">{testimonial.location}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{testimonial.package_name || '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 ${i < testimonial.rating ? 'fill-[#E9C46A] text-[#E9C46A]' : 'text-gray-200'}`} 
                        />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs">{truncateText(testimonial.review, 60)}</TableCell>
                  <TableCell>
                    <Badge variant={testimonial.approved ? 'default' : 'secondary'}>
                      {testimonial.approved ? 'Approved' : 'Pending'}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(testimonial.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!testimonial.approved && (
                          <DropdownMenuItem onClick={() => handleApprove(testimonial.id)}>
                            <Check className="w-4 h-4 mr-2" /> Approve
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleDelete(testimonial.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
